'use client';

// module imports
import { ChevronUp, CreditCard, HelpCircle, Home, LogOut, Menu, Presentation, User, UserCircle2, Users, X } from 'lucide-react';
import { signOut, useSession } from 'next-auth/react';
import Image from 'next/image';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

// file imports
import { getUser } from '@/redux/slices/authSlice';
import type { AppDispatch, RootState } from '@/redux/store';

const navGroups = [
  [
    { label: 'Home', icon: Home, href: '/dashboard' },
    { label: 'Event Planner', icon: UserCircle2, href: '/events' },
  ],
  [
    { label: 'My Profile', icon: User, href: '/profile' },
    { label: 'Presentation', icon: Presentation, href: '/presentations' },
    { label: 'Leads', icon: Users, href: '/leads' },
    // { label: 'Community', icon: Users, href: '/community' },
  ],
  [
    { label: 'Plan & Billing', icon: CreditCard, href: '/billing' },
    { label: 'Help', icon: HelpCircle, href: '/help' },
    // { label: 'Referral Program', icon: Share2, href: '/referral' },
  ],
];

const bottomNavItems = [{ label: 'Roadmap', href: 'https://tbos.canny.io/', external: true }];

export default function SidebarLayout({ children }: { children: React.ReactNode }) {
  const { data: session, status } = useSession();
  const router = useRouter();
  const [isProfileDropdownOpen, setIsProfileDropdownOpen] = useState(false);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const dispatch = useDispatch<AppDispatch>();
  const { data: user } = useSelector((state: RootState) => state.auth);

  // Fetch user data and redirect to login if not authenticated
  useEffect(() => {
    if (status === 'authenticated') {
      dispatch(getUser());
    } else if (status === 'unauthenticated') {
      router.push('/login');
    }
  }, [status, router, dispatch]);

  // Show loading state while checking authentication
  if (status === 'loading') {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#fafbfc]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-2 border-gray-300 border-t-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  const profilePhoto = user?.profilePhoto || session?.user?.profilePhoto || 'https://cdn.pixabay.com/photo/2015/10/05/22/37/blank-profile-picture-973460_640.png';
  const userName = user?.name || session?.user?.name || 'User';
  const userSubscription = user?.subscription?.plan?.charAt(0).toUpperCase() + user?.subscription?.plan?.slice(1) || '';

  const handleLogout = async () => {
    try {
      await signOut({ redirect: false });
      router.push('/login');
    } catch (error) {
      console.error('Logout failed:', error);
      // Fallback redirect
      router.push('/login');
    }
  };

  const profileDropdownItems = [
    { label: 'Your public profile', icon: User, href: '/profile' },
    // { label: 'Account Settings', icon: Settings, href: '/settings' },
    { label: 'Help', icon: HelpCircle, href: '/help' },
    { label: 'Log Out', icon: LogOut, action: handleLogout },
  ];

  return (
    <div className="flex min-h-screen h-screen bg-[#fafbfc]">
      {/* Mobile Menu Button */}
      <button onClick={() => setIsSidebarOpen(true)} className={`lg:hidden fixed top-4 left-4 z-50 p-2 bg-white rounded-lg shadow-md transition-opacity duration-300 ${isSidebarOpen ? 'opacity-0 pointer-events-none' : 'opacity-100'}`} aria-label="Open menu">
        <Menu size={24} />
      </button>

      {/* Sidebar */}
      <aside className={`w-64 bg-white border-r flex flex-col justify-between py-0 fixed lg:relative z-40 h-full transform transition-transform duration-300 ease-in-out ${isSidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}`}>
        {/* Mobile Close Button */}
        <button onClick={() => setIsSidebarOpen(false)} className="lg:hidden absolute top-4 right-4 p-2 text-gray-500 hover:text-gray-700" aria-label="Close menu">
          <X size={24} />
        </button>

        <div>
          {/* Logo */}
          <div className="flex items-center pl-4 lg:pl-16 mb-2 px-4 lg:px-10 pt-12 lg:pt-0">
            <Image src="/logo.png" alt="TBOS logo" width={150} height={54} className="w-32 sm:w-40 lg:w-48 h-auto" priority />
          </div>
          {/* Navigation Groups with dividers */}
          <nav className="flex flex-col gap-1">
            {navGroups.map((group, i) => (
              <div key={i}>
                {group.map(({ label, icon: Icon, href }) => (
                  <Link key={label} href={href} onClick={() => setIsSidebarOpen(false)} className="flex items-center gap-3 px-4 lg:px-14 py-2 rounded-lg text-[#343741] text-sm lg:text-[16px] hover:bg-[#f2f5fa] transition">
                    <Icon size={18} className="lg:w-5 lg:h-5" />
                    <span>{label}</span>
                  </Link>
                ))}
                {/* Render divider except after the last group */}
                {i !== navGroups.length - 1 && <hr className="my-3 mx-4 lg:mx-8 border-t border-gray-200" />}
              </div>
            ))}
          </nav>
        </div>
        {/* Bottom Nav */}
        <div>
          <nav className="flex flex-col gap-1 mb-6">
            {bottomNavItems.map(({ label, href, external }) =>
              external ? (
                <a key={label} href={href} target="_blank" rel="noopener noreferrer" className="flex items-center justify-center px-4 lg:px-14 py-2 text-blue-600 text-sm lg:text-[16px]">
                  <span>{label}</span>
                </a>
              ) : (
                <Link key={label} href={href} onClick={() => setIsSidebarOpen(false)} className="flex items-center justify-center px-4 lg:px-14 py-2 text-blue-600 text-sm lg:text-[16px]">
                  <span>{label}</span>
                </Link>
              )
            )}
          </nav>
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 flex flex-col lg:ml-0">
        {/* Mobile Header */}
        <div className="lg:hidden flex justify-between items-center p-4 bg-white border-b">
          <div className="flex items-center gap-2 ml-12">
            <div className="text-sm">
              <div className="font-semibold text-gray-900">{userName}</div>
              <div className="text-gray-500 text-xs">{userSubscription}</div>
            </div>
          </div>
          <div className="relative">
            <button onClick={() => setIsProfileDropdownOpen(!isProfileDropdownOpen)} className="p-2 text-gray-500 hover:text-gray-700" aria-label="Profile menu">
              <ChevronUp size={20} className={`transition-transform ${isProfileDropdownOpen ? 'rotate-0' : 'rotate-180'}`} />
            </button>
            {isProfileDropdownOpen && (
              <div className="absolute right-0 mt-2 w-56 bg-white rounded-lg shadow-lg border border-gray-200 py-2 z-50">
                {profileDropdownItems.map(({ label, icon: Icon, href, action }) => (
                  <div key={label}>
                    {action ? (
                      <button
                        onClick={() => {
                          action();
                          setIsProfileDropdownOpen(false);
                        }}
                        className="flex items-center gap-3 px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 w-full text-left cursor-pointer"
                      >
                        <Icon size={16} />
                        <span>{label}</span>
                      </button>
                    ) : (
                      <Link href={href!} onClick={() => setIsProfileDropdownOpen(false)} className="flex items-center gap-3 px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">
                        <Icon size={16} />
                        <span>{label}</span>
                      </Link>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Desktop Profile Dropdown */}
        <div className="hidden lg:block absolute top-7 right-12 flex gap-2 items-center">
          <div className="relative">
            <div className="flex items-center gap-2 cursor-pointer" onClick={() => setIsProfileDropdownOpen(!isProfileDropdownOpen)}>
              <Image src={profilePhoto} alt="User" width={32} height={32} className="rounded-full border-2 border-white" />
              <div className="text-sm">
                <div className="font-semibold text-gray-900">{userName}</div>
                <div className="text-gray-500 text-xs">{userSubscription}</div>
              </div>
              <ChevronUp size={16} className={`transition-transform ${isProfileDropdownOpen ? 'rotate-0' : 'rotate-180'}`} />
            </div>

            {/* Dropdown Menu */}
            {isProfileDropdownOpen && (
              <div className="absolute right-0 mt-2 w-56 bg-white rounded-lg shadow-lg border border-gray-200 py-2 z-50">
                {profileDropdownItems.map(({ label, icon: Icon, href, action }) => (
                  <div key={label}>
                    {action ? (
                      <button
                        onClick={() => {
                          action();
                          setIsProfileDropdownOpen(false);
                        }}
                        className="flex items-center gap-3 px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 w-full text-left cursor-pointer"
                      >
                        <Icon size={16} />
                        <span>{label}</span>
                      </button>
                    ) : (
                      <Link href={href!} onClick={() => setIsProfileDropdownOpen(false)} className="flex items-center gap-3 px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">
                        <Icon size={16} />
                        <span>{label}</span>
                      </Link>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Page Content */}
        <div className="flex-1">{children}</div>
      </main>

      {/* Overlay to close dropdown when clicking outside */}
      {isProfileDropdownOpen && <div className="fixed inset-0 z-40" onClick={() => setIsProfileDropdownOpen(false)} />}
    </div>
  );
}
